import React, { useEffect, useRef, useState } from 'react';
import { Button, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { Popper } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import TranslationConstant from '@/common/constant/TranslationConstant';
import DataChangePleaseReloadModal from '@/component/common/modal/DataChangePleaseReloadModal';


import { FormProvider, useForm } from 'react-hook-form';
import Utils from '@/common/utils/utils';
import trI18n from '@/common/utils/trI18n';
import { tr } from '@/common/utils/tr';
import { useTranslationSubscription } from '@/hooks/useTranslation';
import useRevalidateOnTranslation from '@/hooks/useRevalidateOnTranslation';
import FormInput from '@/component/common/form/input';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { userAtom } from '@/state/atom/userAtom';
import Constant from '@/common/constant/Constant';
import './UserProfile.css';
import CropImage from '@/component/common/form/crop-image';
import { editUserProfile } from '@/service/userService';
import { markSelfUpdatingMainLanguage } from '@/component/common/layout/header/handler/HeaderTopHandler';
import 'react-phone-input-2/lib/style.css'
import FormInputWithOnChange from '../common/form/input-with-onchange';
import { closePanelTabSelector } from '@/state/selector/panelSelector';
import { uploadFileToS3 } from '@/service/s3Service';
import { useSnackbar } from 'notistack';
import schema from '@/schema/UserProfileSchema';
import { yupResolver } from '@hookform/resolvers';
import { buildSnackbarMessage } from '@/component/common/SnackbarMessage';

const DEFAULT_MAIN_LANGUAGE = TranslationConstant.DEFAULT_MAIN_LANGUAGE;
const MAIN_LANGUAGE_DISPLAY_JP = TranslationConstant.TRANSLATION_LANG_DISPLAY_JP;
const MAIN_LANGUAGE_CODE_LIST = Object.keys(MAIN_LANGUAGE_DISPLAY_JP);

// Custom Popper with elevated z-index so the dropdown renders above the sidebar menu (MUI v4 fix).
const HighZIndexPopper = (props) => <Popper {...props} style={{ ...props.style, zIndex: 1400 }} placement={props.placement} className={`${props.className || ''} mainLanguage-autocomplete-popper`} />;

export default function UserProfileTab(props) {
    // Re-render when the translation dictionary loads/extends so tr() labels update.
    useTranslationSubscription();

    // common user state state
    const [userDetail, setUserDetail] = useRecoilState(userAtom);
	const inputFileRef = useRef(null);
	const [inputImage, setInputImage] = useState('');
	const [errorImage, setErrorImage] = useState(false);
	const [openCrop, setOpenCrop] = useState(false);
	const [imagePreview, setImagePreview] = useState(userDetail.avatar);
	const [croppedImage, setCroppedImage] = useState(null);
    // CR-2026-004: main language (主言語). Stored in userSetting.mainLanguage (AWSJSON).
    const currentUserSetting = Utils.convertJSONDataToObject(userDetail?.userSetting) || {};
    const [mainLanguage, setMainLanguage] = useState(
        currentUserSetting.mainLanguage || DEFAULT_MAIN_LANGUAGE
    );
    const [isOpenReloadDialog, setIsOpenReloadDialog] = useState(false);
    const [mainLanguageError, setMainLanguageError] = useState('');
    // Track the raw input text so we can detect when user types something invalid.
    const [mainLanguageInputValue, setMainLanguageInputValue] = useState(
        MAIN_LANGUAGE_DISPLAY_JP[currentUserSetting.mainLanguage || DEFAULT_MAIN_LANGUAGE] || ''
    );

    const closePanelTab = useSetRecoilState(closePanelTabSelector);
	const methods = useForm({
        mode: 'all',
        resolver: yupResolver(schema),
        defaultValues: {
            lastName: userDetail?.lastName
                ? Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, userDetail?.lastName)
                : '',
            firstName: userDetail?.firstName
                ? Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, userDetail?.firstName)
                : '',
            email: userDetail?.mail ? userDetail?.mail : '',

        }
    });
	const { handleSubmit, getValues, setValue, register, errors, formState, reset, trigger } = methods;
    // Rebuild error messages that are already shown once the translation dictionary loads.
    useRevalidateOnTranslation(errors, trigger);
	const { setIsLoading } = props;
    const { enqueueSnackbar } = useSnackbar();

    
    const handleSubmitEditUserDetail = async () => {
        // check error crop images
        const formData = getValues();
        if (errorImage) {
            return;
        }
        setIsLoading(true);

        let userForUpdate = {
            id: userDetail.id,
            userId: userDetail.mail,
            firstName: formData.firstName,
            lastName: formData.lastName,
            tel: formData?.phoneNumber || userDetail?.tel,
            image: userDetail.image,
            mainLanguage: mainLanguage
        };

        if (croppedImage && croppedImage.file) {
            let filePath = `user_avatar/${userDetail.mail}${new Date().getTime()}`;
            let fileUpload = {...croppedImage}
            fileUpload.key = filePath;

            let uploadAvatar = await uploadFileToS3(filePath, fileUpload);  
            if (!uploadAvatar) {
                setIsLoading(false);
                return;
            }
            userForUpdate.image = filePath;
        }

        const prevMainLanguage = currentUserSetting.mainLanguage || DEFAULT_MAIN_LANGUAGE;
        // Mark before the mutation so the onUserDeleted subscription echo (this same
        // updateUser call, received back on this same tab) does not also open the
        // cross-tab reload dialog on top of isOpenReloadDialog below.
        if (mainLanguage !== prevMainLanguage) {
            markSelfUpdatingMainLanguage();
        }
        const updateResult = await editUserProfile(props.api, userForUpdate);
        if(updateResult) {
            enqueueSnackbar(buildSnackbarMessage('COMMSG047'), {
                variant: 'success',
                autoHideDuration: 3000
            });
            reset(formData);
        }

        // CR-2026-004: when the main language changed (in either direction — JP→non-JP or
        // non-JP→JP), show the confirm dialog (COMMSG030) so the user reloads and the UI
        // switches to the new language (actual translation handled by MLT-001-F1).
        // OK on the dialog triggers window.reload().
        if (
            updateResult &&
            mainLanguage !== prevMainLanguage
        ) {
            markSelfUpdatingMainLanguage();
            setIsLoading(false);
            setIsOpenReloadDialog(true);
            return;
        }

        window.location.reload();
        return
    };

    useEffect(() => {
        //chưa dùng phoneNumber nên tạm thời ẩn đi
        // register('phoneNumber');
        // setValue('phoneNumber', userDetail?.tel ? userDetail?.tel : '');

        let lastName = userDetail?.lastName
            ? Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, userDetail?.lastName)
            : '';
        let firstName = userDetail?.firstName
            ? Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, userDetail?.firstName)
            : '';
        setValue('email', userDetail?.mail ? userDetail?.mail : '');
        setValue('lastName', lastName);
        setValue('firstName', firstName);

    }, []);

    useEffect(() => {
        if (props.submitChangeUserProfileStatus) {
            handleSubmitEditUserDetail().then(res => {
                if (props.submitChangeUserProfileStatus === 1) {
                    props.setTabSelected('persionalSetting');
                } else {
                    closePanelTab({
                        event: null,
                        panelType: Constant.PANEL_TYPE.PERSONAL_MANAGEMENT,
                        panelId: props.panelId,
                        tabId: 'system_user_profile'
                    });
                }
                props.setSubmitChangeUserProfileStatus(0);
            });
        }
    }, [props.submitChangeUserProfileStatus]);

    const onFileChange = event => {
        let file = event.target.files[0];
        // check file input
        if (Utils.isEmptyObject(file)) {
            return;
        }
        props.setIsProfileHasChange(true)
        Utils.readFileAsArrayBuffer(file).then(reader => {
            let buffer = new Buffer(reader.result);
            let imgBase64 = buffer.toString('base64');

            setInputImage(`data:${file.type};base64,${imgBase64}`);
            setOpenCrop(true);
            event.target.value = '';
        });
    };



    return (
        <div className="userProfileTab scrollTab">
            <div className="settingFrame">
                <FormProvider {...methods}>
                    <div className="userProfileForm">
                        <div className="userProfileHeader">
                            <div className="userAvatar">
                                <img src={imagePreview} alt="" />
                                <div className="userAvatarInput">
                                    <input
                                        id="contained-button-file-user-profile"
                                        type="file"
                                        accept="image/*"
                                        className="updateFile"
                                        onChange={onFileChange}
                                        ref={inputFileRef}
                                    />
                                    <label>
                                        <Button
                                            onClick={() => inputFileRef.current.click()}
                                            variant="contained"
                                            component="span"
                                        >
                                            {tr('画像ファイルを選択')}
                                        </Button>
                                    </label>
                                    {errorImage && (
                                        <div className="imageError">{trI18n('USER02MSG001')}</div>
                                    )}
                                </div>
                            </div>

                            <div className="form-list-header-control customHeaderbtn">
                                <Button
                                    className="btn-control btn-send  btn-primary btn-hg32"
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSubmit(handleSubmitEditUserDetail)}
                                    disabled={
                                        !formState.isValid ||
                                        !!mainLanguageError ||
                                        (!formState.isDirty &&
                                            !croppedImage &&
                                            mainLanguage === (currentUserSetting.mainLanguage || DEFAULT_MAIN_LANGUAGE))
                                    }
                                >
                                    {tr('反映')}
                                </Button>
                            </div>
                        </div>

                        <div className="userInfo">
                            <div className="userInfoName">
                                <div className="userInfoItem userInfoLastName">
                                    <div className="userInfoNameLabel">
                                        <span>{tr('姓')}</span>
                                        <span className="star-required">*</span>
                                    </div>
                                    <FormInputWithOnChange
                                        name="lastName"
                                        defaultValue={
                                            userDetail?.lastName
                                                ? Utils.getTextByLanguage(
                                                      Constant.LANGUAGE_TYPE.JP,
                                                      userDetail?.lastName
                                                  )
                                                : ''
                                        }
                                        onChange= {e => props.setIsProfileHasChange(true)}
                                        className="form-control form-control-wrapInput form-control-hg40"
                                        variant="outlined"
                                        size="small"
                                        errorlist={errors}
                                    />
                                </div>
                                <div className="userInfoItem customItemMiddle userInfoFirstName">
                                    <div className="userInfoNameLabel">
                                        <span>{tr('名')}</span>
                                        <span className="star-required">*</span>
                                    </div>
                                    <FormInputWithOnChange
                                        name="firstName"
                                        defaultValue={
                                            userDetail?.firstName
                                                ? Utils.getTextByLanguage(
                                                      Constant.LANGUAGE_TYPE.JP,
                                                      userDetail?.firstName
                                                  )
                                                : ''
                                        }
                                        onChange= {e => props.setIsProfileHasChange(true)}
                                        className="form-control form-control-wrapInput form-control-hg40"
                                        variant="outlined"
                                        size="small"
                                        errorlist={errors}
                                    />
                                </div>
                            </div>

                            <div className="userInfoItem">
                                <div className="userInfoNameLabel">
                                    <span>{tr('メールアドレス')}</span>
                                </div>
                                <div>
                                    <FormInput
                                        name="email"
                                        className="form-control form-control-wrapInput form-control-hg40"
                                        defaultValue={userDetail.mail}
                                        variant="outlined"
                                        size="small"
                                        disabled
                                    />
                                </div>
                            </div>

                            {/* CR-2026-004: 主言語 (main language) — single-select searchable. */}
                            <div className="userInfoItem mainLanguageSetting">
                                <div className="userInfoNameLabel">
                                    <span>{trI18n('LABEL-MAIN-LANGUAGE')}</span>
                                    <span className="star-required">*</span>
                                </div>
                                <div>
                                    <Autocomplete
                                        options={MAIN_LANGUAGE_CODE_LIST}
                                        getOptionLabel={option =>
                                            option ? MAIN_LANGUAGE_DISPLAY_JP[option] || '' : ''
                                        }
                                        getOptionSelected={(option, val) =>
                                            option === val
                                        }
                                        value={
                                            MAIN_LANGUAGE_CODE_LIST.includes(mainLanguage)
                                                ? mainLanguage
                                                : null
                                        }
                                        inputValue={mainLanguageInputValue}
                                        onInputChange={(e, newInputValue, reason) => {
                                            setMainLanguageInputValue(newInputValue);
                                            // When user clears the input box entirely, show error immediately.
                                            if (reason === 'input' && newInputValue === '') {
                                                setMainLanguageError(trI18n('MSG-CM-REQUIRED-INPUT'));
                                            } else {
                                                setMainLanguageError('');
                                            }
                                        }}
                                        // Ensure the dropdown popup renders above the sidebar menu (MUI v4 z-index fix).
                                        PopperComponent={HighZIndexPopper}
                                        popupIcon={<ExpandMoreIcon fontSize="small" />}
                                        onChange={(e, selected) => {
                                            if (!selected) {
                                                // User cleared the field — show required error.
                                                setMainLanguage('');
                                                setMainLanguageError(trI18n('MSG-CM-REQUIRED-INPUT'));
                                                props.setIsProfileHasChange(true);
                                                return;
                                            }
                                            setMainLanguage(selected);
                                            setMainLanguageError('');
                                            props.setIsProfileHasChange(true);
                                        }}
                                        onBlur={() => {
                                            // When user typed free text that doesn't match any option,
                                            // MUI v4 reverts the input display but mainLanguage stays valid.
                                            // Detect mismatch: if input text doesn't match the current selection label.
                                            const currentLabel = mainLanguage
                                                ? MAIN_LANGUAGE_DISPLAY_JP[mainLanguage] || ''
                                                : '';
                                            if (!mainLanguageInputValue || mainLanguageInputValue !== currentLabel) {
                                                if (!mainLanguage || !MAIN_LANGUAGE_CODE_LIST.includes(mainLanguage)) {
                                                    setMainLanguageError(trI18n('MSG-CM-REQUIRED-INPUT'));
                                                } else {
                                                    // Input was partially typed but reverts to valid — restore display and clear error.
                                                    setMainLanguageInputValue(currentLabel);
                                                    setMainLanguageError('');
                                                }
                                            }
                                        }}
                                        noOptionsText={trI18n('COMMSG001')}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                size="small"
                                                className="form-control form-control-wrapInput form-control-hg40"
                                                error={!!mainLanguageError}
                                            />
                                        )}
                                    />
                                    {mainLanguageError && (
                                        <p className="MuiFormHelperText-root MuiFormHelperText-contained Mui-error" style={{ color: '#f44336', margin: '4px 14px 0', fontSize: '0.75rem' }}>{mainLanguageError}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <DataChangePleaseReloadModal
                        isOpen={isOpenReloadDialog}
                        setIsOpen={setIsOpenReloadDialog}
                    />
                    </div> 
                </FormProvider>
            </div>
            {/* crop image */}
            <CropImage
                openCrop={openCrop}
                setOpenCrop={setOpenCrop}
                aspect={1}
                cropShape={'round'}
                inputImage={inputImage}
                setInputImage={setInputImage}
                setImagePreview={setImagePreview}
                setCroppedImage={setCroppedImage}
                setErrorImage={setErrorImage}
            />
        </div>
    );
}
